import React from 'react';

type ItemNode = {
  itemKey: string;
  items?: ItemNode[];
};

type ItemKeyToParentKey = Record<string, string | undefined>;

export type SidebarStructure = {
  items: ItemNode[];
  keyToParentKey: ItemKeyToParentKey;
};

const readItemNodesRecursively = (
  parentItemNode: ItemNode,
  children: React.ReactNode,
) => {
  if (!children) {
    return;
  }

  React.Children.toArray(children).forEach(child => {
    if (!React.isValidElement(child)) {
      return;
    }

    const itemKey = child?.props?.itemKey;
    const itemChildren = child?.props?.children;

    if (typeof itemKey === 'string' && itemKey !== parentItemNode.itemKey) {
      const itemNode = { itemKey };
      (parentItemNode.items ??= []).push(itemNode);
      readItemNodesRecursively(itemNode, itemChildren);
    } else {
      readItemNodesRecursively(parentItemNode, itemChildren);
    }
  });
};

const readParentKeysRecursively = (
  keyToParentKey: ItemKeyToParentKey,
  itemNodes: ItemNode[],
  parentItemKey?: string,
) => {
  for (const { itemKey, items } of itemNodes) {
    keyToParentKey[itemKey] = parentItemKey;

    if (items) {
      readParentKeysRecursively(keyToParentKey, items, itemKey);
    }
  }
};

/**
 * Recursively walks react children to create a tree of sidebar item nodes and
 * their parent-child relationships.
 */
export const extractItemNodes = (
  children: React.ReactNode | React.ReactNode[],
): SidebarStructure => {
  const rootNode: ItemNode = { itemKey: 'rootMenu' }; // TODO: use a symbol
  readItemNodesRecursively(rootNode, children);

  const items = rootNode.items ?? [];
  const keyToParentKey: ItemKeyToParentKey = Object.create(null);
  readParentKeysRecursively(keyToParentKey, items);

  return { items, keyToParentKey };
};

export const getItemPath = (
  keyToParentKey: ItemKeyToParentKey,
  itemKey?: string,
) => {
  const path = [] as string[];

  while (itemKey !== undefined) {
    path.unshift(itemKey);
    itemKey = keyToParentKey[itemKey];
  }

  return path;
};

export const isMenu = (keyToParentKey: ItemKeyToParentKey, itemKey: string) =>
  Object.values(keyToParentKey).includes(itemKey);

export const getMenuPath = (
  keyToParentKey: ItemKeyToParentKey,
  itemKey?: string,
) => {
  const path = getItemPath(keyToParentKey, itemKey);

  if (path.length > 0 && !isMenu(keyToParentKey, path[path.length - 1])) {
    path.pop();
  }

  return path;
};

export const getMaxDepth = (items?: ItemNode[]) => {
  let maxDepth = 0;

  if (items?.length) {
    for (const item of items) {
      maxDepth = Math.max(maxDepth, getMaxDepth(item.items) + 1);
    }
  }

  return maxDepth;
};

/**
 * Returns a new object with only the specified keys from the original one.
 */
export function pick<T extends Record<string, any>, K extends keyof T>(
  object: T,
  keys: K[],
): Pick<T, K>;
export function pick<T extends Record<string, any>>(
  object: T,
  keys: string[],
): Partial<T>;
export function pick(object: Record<string, any>, keys: string[]) {
  // Probably should use `lodash.pick` but it's too smart with its key handing (
  // lodash keys can be paths like `a.b.c`). Since we're using this to pick
  // keys from a record which gets created from consumer input, we can't be sure
  // that consumers won't use keys that look like paths.
  const result: Record<string, any> = {};

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      result[key] = object[key];
    }
  }

  return result;
}
